import { useMemo, useState } from 'react'
import { useGetFubUsers } from '@forwardflow/queries/onboarding.queries'
import { useSubscriptionPackageLists } from '@forwardflow/hooks/customHooks'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import {
   ADDITIONAL_SEAT_COST,
   BASE_SEAT_COST,
} from '@forwardflow/constants/onboarding.constants'
import { TFubUser } from '@forwardflow/types/onboarding'
import { Screen } from '../frame/SetupFrame'
import { useStepFooter } from '../../hooks/useStepFooter'
import {
   Avatar,
   FieldLabel,
   Row,
   RowCard,
   SearchPick,
   Skeleton,
} from '../ui/primitives'
import { Icon } from '../ui/Icon'
import {
   fullName,
   MAX_SEATS,
   membersOf,
   ownerOf,
   seatCostFor,
} from '../../utils/seats'
import { pricingOf } from '../../utils/budget'

/**
 * Step 3: who else should get leads.
 *
 * Teammates are picked from the connected Follow Up Boss account; each one
 * is a seat. The plan is never asked for: one seat is Solo, more is Team.
 * The seat count and the tier go on the record for the payment step.
 */
export const TeamStep = () => {
   const { onboardingData, getNextStep, setInvitedUsers, setPricingData } =
      useOnboardingStoreV2()
   const { organizationId, isFUBConnected, invitedUsers } = onboardingData
   const { data, isLoading } = useGetFubUsers(
      organizationId ?? '',
      1000,
      1,
      isFUBConnected
   )
   const { data: packages } = useSubscriptionPackageLists({
      page: 1,
      limit: 10,
   })
   const users: TFubUser[] = useMemo(() => data?.data || [], [data])
   const owner = ownerOf(invitedUsers)
   const savedMembers = membersOf(invitedUsers)
   const [memberIds, setMemberIds] = useState<string[]>(() =>
      savedMembers.map((u) => String(u.id))
   )

   const byId = (id: string) =>
      users.find((u) => String(u.id) === id) ||
      savedMembers.find((u) => String(u.id) === id)
   const available = users.filter(
      (u) =>
         String(u.id) !== String(owner?.id) && !memberIds.includes(String(u.id))
   )
   const seats = Math.min(MAX_SEATS, 1 + memberIds.length)
   const cost = seatCostFor(seats)

   const add = (id: string) => {
      if (memberIds.includes(id) || memberIds.length + 1 >= MAX_SEATS) return
      setMemberIds((m) => [...m, id])
   }
   const remove = (id: string) => setMemberIds((m) => m.filter((x) => x !== id))

   const next = () => {
      const members = memberIds
         .map(byId)
         .filter((u): u is TFubUser => Boolean(u))
      setInvitedUsers([
         ...members.map((u) => ({ ...u, isOwner: false })),
         ...(owner ? [owner] : []),
      ])
      const pricing = pricingOf(onboardingData.pricingData)
      const tierId = packages?.pricingTiers?.[0]?.id ?? pricing.selectedTierId
      setPricingData({
         ...pricing,
         seatsCount: 1 + members.length,
         selectedTierId: tierId,
      })
      getNextStep()
   }

   useStepFooter(
      seats === 1 ? 'Continue with just me' : `Continue with ${seats} seats`,
      next,
      {
         hint:
            seats === 1
               ? `$${BASE_SEAT_COST}/mo for one seat. You can always add teammates later from Settings.`
               : `$${BASE_SEAT_COST}/mo for you, $${ADDITIONAL_SEAT_COST}/mo per teammate · $${cost}/mo total.`,
         testId: 'continue-team',
      }
   )

   return (
      <Screen
         stepKey="your-team"
         kind="team"
         title="Who else should get leads?"
         lede="Pick teammates from Follow Up Boss. Leads are shared with everyone here. You can always add more teammates from Settings."
         width="sm"
      >
         <FieldLabel>Add from Follow Up Boss</FieldLabel>
         {isLoading && !users.length ? (
            <Skeleton className="h-12 rounded-2xl" />
         ) : (
            <SearchPick
               items={available.map((p) => ({
                  id: String(p.id),
                  label: fullName(p),
                  sub: p.email,
               }))}
               placeholder="Search by name or email"
               emptyLabel={
                  users.length
                     ? 'Nobody else in your Follow Up Boss account. Teammates can be added later from Settings.'
                     : 'Still reading your team from Follow Up Boss'
               }
               onPick={add}
               testId="team-search"
               renderItem={(r) => (
                  <>
                     <Avatar name={r.label} size={28} />
                     <span className="flex-1 min-w-0 truncate">{r.label}</span>
                     <span className="text-[12px] text-[var(--ob-text-3)] truncate">
                        {r.sub}
                     </span>
                  </>
               )}
            />
         )}
         <RowCard className="mt-4" testId="team-list">
            {owner && (
               <Row className="min-h-[56px] py-1">
                  <Avatar name={fullName(owner)} size={32} />
                  <span className="flex-1 min-w-0">
                     <span className="block text-[14px] font-semibold truncate">
                        {fullName(owner)}
                     </span>
                     <span className="block text-[12px] text-[var(--ob-text-3)] truncate">
                        You
                     </span>
                  </span>
                  <span className="text-[12px] text-[var(--ob-text-3)]">
                     ${BASE_SEAT_COST}/mo
                  </span>
               </Row>
            )}
            {memberIds.map((id) => {
               const p = byId(id)
               if (!p) return null
               return (
                  <Row key={id} className="min-h-[56px] py-1">
                     <Avatar name={fullName(p)} size={32} />
                     <span className="flex-1 min-w-0">
                        <span className="block text-[14px] font-semibold truncate">
                           {fullName(p)}
                        </span>
                        <span className="block text-[12px] text-[var(--ob-text-3)] truncate">
                           {p.email}
                        </span>
                     </span>
                     <span className="text-[12px] text-[var(--ob-text-3)]">
                        +${ADDITIONAL_SEAT_COST}/mo
                     </span>
                     <button
                        type="button"
                        aria-label={`Remove ${fullName(p)}`}
                        onClick={() => remove(id)}
                        className="w-8 h-8 rounded-full text-[var(--ob-text-3)] hover:bg-[var(--ob-surface-2)] hover:text-white flex items-center justify-center"
                        data-testid={`remove-${id}`}
                     >
                        <Icon name="close" size={14} />
                     </button>
                  </Row>
               )
            })}
         </RowCard>
         <p
            className="mt-3 text-center text-[12px] text-[var(--ob-text-3)]"
            data-testid="seat-cost"
         >
            {seats === 1 ? 'Just you' : `You + ${seats - 1}`} · ${cost}/mo ·
            teammates get an invite once you launch.
         </p>
      </Screen>
   )
}
